// Merged "AutoStory + WhatChanged" surface for the home page.
// Risk-first ordering: blocking rules → new violations → biggest mover since
// last visit. Replaces the two competing narrative cards that previously
// stacked at the top of /.

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import {
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-react';
import type { PositionWithTv } from '@/lib/api';
import type { Snapshot, Violation } from '@t212/shared';
import { fmtRelative, fmtSignedPercent } from '@/lib/format';
import { cn } from '@/lib/cn';

const STORAGE_KEY = 't212:lastSeen';

type Props = {
  positions: PositionWithTv[];
  snapshots: Snapshot[];
  violations: Violation[];
};

type LastSeen = { at: string; ruleIds: string[]; values: Record<string, number> };

function readLastSeen(): LastSeen {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { at: new Date().toISOString(), ruleIds: [], values: {} };
    return JSON.parse(raw);
  } catch {
    return { at: new Date().toISOString(), ruleIds: [], values: {} };
  }
}

function writeLastSeen(snap: LastSeen) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch {
    // ignore quota / disabled storage
  }
}

export function TodayCard({ positions, snapshots, violations }: Props) {
  const [last] = useState(() => readLastSeen());
  const [dismissed, setDismissed] = useState(false);

  const currentValues = useMemo(() => {
    const out: Record<string, number> = {};
    for (const p of positions) {
      out[p.instrument.ticker] = p.walletImpact.currentValue;
    }
    return out;
  }, [positions]);

  // Movers since last visit (delta %, not lifetime %).
  const movers = useMemo(() => {
    type Mover = { ticker: string; from: number; to: number; pct: number };
    const out: Mover[] = [];
    for (const p of positions) {
      const before = last.values[p.instrument.ticker];
      const now = p.walletImpact.currentValue;
      if (typeof before === 'number' && before > 0) {
        const pct = ((now - before) / before) * 100;
        if (Math.abs(pct) >= 0.4) out.push({ ticker: p.instrument.ticker, from: before, to: now, pct });
      }
    }
    return out.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct)).slice(0, 3);
  }, [positions, last.values]);

  const newViolations = useMemo(
    () => violations.filter((v) => !last.ruleIds.includes(v.ruleId)),
    [violations, last.ruleIds],
  );

  const blockCount = violations.filter((v) => v.severity === 'block').length;
  const warnCount = violations.filter((v) => v.severity === 'warn').length;

  // Risk-first headline.
  const headline = useMemo(() => {
    if (blockCount > 0) {
      return {
        text: `${blockCount} blocking rule${blockCount === 1 ? '' : 's'} firing — review before adding risk`,
        tone: 'block' as const,
      };
    }
    if (warnCount > 0) {
      return {
        text: `${warnCount} warning${warnCount === 1 ? '' : 's'} active`,
        tone: 'warn' as const,
      };
    }
    if (newViolations.length > 0) {
      return {
        text: `${newViolations.length} new rule trip${newViolations.length === 1 ? '' : 's'} since your last visit`,
        tone: 'warn' as const,
      };
    }
    if (positions.length === 0) {
      return null;
    }
    return { text: 'All rules pass.', tone: 'ok' as const };
  }, [blockCount, warnCount, newViolations.length, positions.length]);

  // Persist current state after first render so a refresh doesn't clobber deltas.
  useEffect(() => {
    if (positions.length === 0) return;
    const ruleIds = [...new Set(violations.map((v) => v.ruleId))];
    const t = setTimeout(() => {
      writeLastSeen({ at: new Date().toISOString(), ruleIds, values: currentValues });
    }, 1500);
    return () => clearTimeout(t);
  }, [positions.length, violations, currentValues]);

  const sinceText = last.at ? fmtRelative(last.at) : 'a moment ago';
  const hasSnaps = snapshots.length > 0;

  if (!headline || dismissed) return null;
  if (!hasSnaps && newViolations.length === 0 && movers.length === 0 && blockCount === 0 && warnCount === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -2 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'rounded-xl border bg-gradient-to-br from-zinc-950 px-4 py-3 space-y-2.5',
        headline.tone === 'block'
          ? 'border-rose-500/40 to-rose-500/[0.05]'
          : headline.tone === 'warn'
            ? 'border-amber-500/40 to-amber-500/[0.04]'
            : 'border-zinc-800 to-indigo-500/[0.03]',
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <Clock className="size-3.5 text-zinc-400" />
          <span className="text-[11px] uppercase tracking-wider text-zinc-400">
            Since {sinceText}
          </span>
        </div>
        <p
          className={cn(
            'text-sm flex-1 leading-snug',
            headline.tone === 'block'
              ? 'text-rose-100'
              : headline.tone === 'warn'
                ? 'text-amber-100'
                : 'text-zinc-200',
          )}
        >
          {headline.tone === 'block' || headline.tone === 'warn' ? (
            <ShieldAlert className="inline size-3.5 -mt-0.5 mr-1.5" />
          ) : (
            <Sparkles className="inline size-3.5 -mt-0.5 mr-1.5 text-indigo-300" />
          )}
          {headline.text}
        </p>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="p-1.5 rounded text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900"
          aria-label="Dismiss today summary"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {(newViolations.length > 0 || movers.length > 0) && (
        <div className="flex items-center gap-2 flex-wrap pl-6">
          {newViolations.slice(0, 3).map((v) => (
            <Link
              key={v.ruleId + (v.ticker ?? '')}
              to={v.ticker ? '/positions/$ticker' : '/rules'}
              params={v.ticker ? { ticker: v.ticker } : undefined}
              className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20 transition-colors"
            >
              <ShieldAlert className="size-3" />
              {v.ruleId}
              {v.ticker ? ` · ${v.ticker.split('_')[0]}` : ''}
            </Link>
          ))}
          {movers.map((m) => (
            <Link
              key={m.ticker}
              to="/positions/$ticker"
              params={{ ticker: m.ticker }}
              className={cn(
                'inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border hover:opacity-90 transition-opacity num',
                m.pct >= 0
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                  : 'border-rose-500/40 bg-rose-500/10 text-rose-100',
              )}
            >
              {m.pct >= 0 ? (
                <ArrowUpRight className="size-3" />
              ) : (
                <ArrowDownRight className="size-3" />
              )}
              <span className="font-mono">{m.ticker.split('_')[0]}</span>
              {fmtSignedPercent(m.pct)}
            </Link>
          ))}
        </div>
      )}
    </motion.div>
  );
}
